import { writable } from 'svelte/store';
import type { MenuItemListExtendedProps } from 'src/components/Dashboards/dash-types';

/**
 * A single group of items in the kebab (⋮). If `title` is empty and the group
 * is not the first, it renders as a standalone divider (see `ListActions`).
 */
export type ListHeaderMenuGroup = {
	title?: string;
	isGroupTitle?: boolean;
	items: MenuItemListExtendedProps[];
};

export type ListHeaderPrimaryActionVariant =
	| 'premium'
	| 'success'
	| 'danger'
	| 'primary'
	| 'secondary'
	| 'custom'
	| 'danger-secondary';

export type ListHeaderPrimaryAction = {
	label?: string;
	icon?: string;
	variant?: ListHeaderPrimaryActionVariant;
	disabled?: boolean;
	ariaLabel?: string;
	onClick: () => void;
};

/**
 * An arbitrary Svelte component rendered in the tabs action slot next to (or
 * in place of) the default kebab. Used when a list needs richer chrome than
 * data-driven `menuItems` can express — e.g. Devices' combined "All devices"
 * dropdown with bulk actions and view selection sections.
 */
export type ListHeaderExtraAction = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	component: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	props?: Record<string, any>;
	/** Stable key for `#each` re-mount identity. */
	key?: string;
};

/**
 * A list holder publishes its header config here for the shared sub-tabs
 * `Tabs` action slot in `MainLayoutNew` to render. The layout builds the kebab
 * (⋮) menu and any custom slots from this config. Modals/wizards stay in the
 * holder — the config's `onClick` callbacks close over holder state.
 *
 * Holders should clear the config on `onDestroy` (or when the list unmounts)
 * so the layout doesn't render stale controls after route changes.
 *
 * NB: search / filter chrome deliberately stays in each list's own Toolbar
 * (below the tabs), not in this config, so lists that need bespoke filter UX
 * (chips, multiple inputs, modal triggers) keep full control.
 */
export type ListHeaderConfig = {
	menuItems?: ListHeaderMenuGroup[];
	primaryAction?: ListHeaderPrimaryAction | null;
	extraActions?: ListHeaderExtraAction[];
};

/**
 * Internal shape stored in the writable — carries an opaque owner symbol so
 * the `clear()` half of a publisher handle can safely no-op when a different
 * publisher has since taken ownership. The `_owner` field is invisible to
 * consumers (they only read the documented `ListHeaderConfig` fields).
 */
type InternalListHeaderConfig = ListHeaderConfig & { _owner?: symbol };

export const listHeader = writable<InternalListHeaderConfig | null>(null);

/**
 * Create a per-publisher handle for the shared list header.
 *
 * Motivation: Svelte destroys the outgoing component **after** the new one
 * has already mounted, so a naïve `onDestroy(() => listHeader.set(null))`
 * clobbers the next publisher's config that was just published in the same
 * navigation tick. This handle tags every `set` with an owner symbol and
 * only clears the store when the current value still belongs to this owner.
 *
 * Usage:
 *   const header = createListHeaderHandle();
 *   $: if (condition) header.set({ menuItems: [...] });
 *   onDestroy(() => header.clear());
 */
export function createListHeaderHandle() {
	const owner = Symbol('listHeader-owner');
	return {
		set(config: ListHeaderConfig) {
			listHeader.set({ ...config, _owner: owner });
		},
		clear() {
			listHeader.update((current) => (current && current._owner === owner ? null : current));
		},
	};
}
